import { useMemo, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "sonner";
import { useRegister } from "@/features/auth/services";
import { isApiError, type ApiError } from "@/shared/api";
import { useValidateReferralCode } from "@/features/profile/services";
import { CheckCircle2, XCircle, Loader2 as LoaderIcon } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Button } from "@/shared/components/ui/button";
// Temporarily hidden - Google login imports
// import Separator from "./Separator";
// import SignInWithGoogle from "./SignInWIthGoogle";
import { setPendingEmailVerification } from "../utils/email-verification";

const signUpSchema = z
  .object({
    firstName: z.string().trim().min(1, "First name is required"),
    lastName: z.string().trim().min(1, "Last name is required"),
    email: z
      .string()
      .trim()
      .min(1, "Email is required")
      .email("Enter a valid email address"),
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Z]/, "Password must include an uppercase letter")
      .regex(/[a-z]/, "Password must include a lowercase letter")
      .regex(/[0-9]/, "Password must include a number")
      .regex(
        /[^A-Za-z0-9]/,
        "Password must include a non-alphanumeric character"
      ),
    confirmPassword: z.string(),
    referralCode: z.string().optional(),
    termsAccepted: z.boolean().refine((value) => value, {
      message: "You must accept the Terms and Conditions to proceed.",
    }),
  })
  .superRefine((data, ctx) => {
    if (data.password !== data.confirmPassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "The passwords you entered do not match.",
        path: ["confirmPassword"],
      });
    }
  });

type SignUpFormValues = z.infer<typeof signUpSchema>;

const UNKNOWN_ERROR_MESSAGE =
  "Unable to create your account. Please try again in a moment.";

const SIGN_UP_FIELD_NAMES: Array<keyof SignUpFormValues> = [
  "firstName",
  "lastName",
  "email",
  "password",
  "confirmPassword",
  "referralCode",
  "termsAccepted",
];

type RegisterServerError = {
  errorMessage?: string;
  errorName?: string;
  errorCode?: string;
  requestId?: string;
};

const asNonEmptyString = (value: unknown) =>
  typeof value === "string" && value.trim().length > 0 ? value : undefined;

const normalizeFieldErrorMessage = (value: unknown) => {
  if (typeof value === "string") {
    return value;
  }

  if (Array.isArray(value)) {
    return value
      .filter((item): item is string => typeof item === "string")
      .join(" ");
  }

  return undefined;
};

const extractRegisterServerError = (error: ApiError): RegisterServerError => {
  let errorMessage = asNonEmptyString(error.message);
  let errorName = asNonEmptyString(error.errorName);
  let errorCode = asNonEmptyString(error.errorCode);

  if (error.data && typeof error.data === "object") {
    const payload = error.data as Record<string, unknown>;

    if (!errorMessage) {
      errorMessage =
        asNonEmptyString(payload.errorMessage) ??
        asNonEmptyString(payload.message) ??
        errorMessage;
    }

    if (!errorName) {
      errorName = asNonEmptyString(payload.errorName) ?? errorName;
    }

    if (!errorCode) {
      errorCode = asNonEmptyString(payload.errorCode) ?? errorCode;
    }
  }

  return {
    errorMessage,
    errorName,
    errorCode,
    requestId: error.requestId,
  };
};

export default function SignUpForm() {
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [referralValidationState, setReferralValidationState] = useState<
    "idle" | "validating" | "valid" | "invalid"
  >("idle");
  const [referrerName, setReferrerName] = useState<string>("");
  const { mutateAsync: register, isPending } = useRegister();
  const { mutateAsync: validateReferral } = useValidateReferralCode();
  const navigate = useNavigate();

  const form = useForm<SignUpFormValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      referralCode: "",
      termsAccepted: false,
    },
  });

  const passwordValue = form.watch("password") ?? "";
  const confirmPasswordValue = form.watch("confirmPassword") ?? "";
  const referralCodeValue = form.watch("referralCode") ?? "";
  const showPasswordValidation =
    passwordValue.length > 0 || confirmPasswordValue.length > 0;

  // Debounced referral code validation
  useEffect(() => {
    if (!referralCodeValue || referralCodeValue.trim().length === 0) {
      setReferralValidationState("idle");
      setReferrerName("");
      return;
    }

    const timer = setTimeout(async () => {
      setReferralValidationState("validating");
      try {
        const result = await validateReferral({
          referralCode: referralCodeValue.trim(),
        });
        if (result.valid && result.referrer) {
          setReferralValidationState("valid");
          setReferrerName(result.referrer.name);
        } else {
          setReferralValidationState("invalid");
          setReferrerName("");
        }
      } catch {
        setReferralValidationState("invalid");
        setReferrerName("");
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [referralCodeValue, validateReferral]);

  useEffect(() => {
    const storedReferralCode = localStorage.getItem("referral_code");
    if (storedReferralCode) {
      form.setValue("referralCode", storedReferralCode);
    }
  }, [form]);

  const rules = useMemo(
    () => ({
      hasMinLength: passwordValue.length >= 8,
      hasUppercase: /[A-Z]/.test(passwordValue),
      hasLowercase: /[a-z]/.test(passwordValue),
      hasNumber: /[0-9]/.test(passwordValue),
      hasSpecialChar: /[^A-Za-z0-9]/.test(passwordValue),
    }),
    [passwordValue]
  );

  const allValid = Object.values(rules).every(Boolean);
  const passwordsMatch =
    passwordValue !== "" && passwordValue === confirmPasswordValue;

  const containerClass = `flex flex-col p-4 rounded-xl text-xs space-y-1 transition-colors duration-200
    ${allValid ? "border border-success text-success bg-success/20" : "border border-danger text-danger bg-danger/20"}`;

  const itemClass = (ok: boolean) => (ok ? "text-green-600" : "text-red-600");

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmissionError(null);
    form.clearErrors();

    try {
      const payload: {
        firstName: string;
        lastName: string;
        email: string;
        password: string;
        referralCode?: string;
      } = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
      };

      // Only include referralCode if it's valid
      if (values.referralCode && referralValidationState === "valid") {
        payload.referralCode = values.referralCode.trim();
      }

      const normalizedEmail = values.email.trim();
      const response = await register(payload);
      const otpSentAt = Date.now();

      setPendingEmailVerification(normalizedEmail, {
        redirectPath: "/sign-in",
        otpSentAt,
        source: "register",
      });

      toast.success(
        response.user.firstName
          ? `Account created for ${response.user.firstName}. Please verify your email to continue.`
          : "Account created. Please verify your email to continue."
      );

      navigate("/email-verification", {
        replace: true,
        state: {
          email: normalizedEmail,
          redirectPath: "/sign-in",
          otpSentAt,
          source: "register",
        },
      });
    } catch (error) {
      if (isApiError(error)) {
        const { errorMessage, errorName, errorCode, requestId } =
          extractRegisterServerError(error);
        const message = errorMessage ?? UNKNOWN_ERROR_MESSAGE;
        toast.error(message, {
          description: requestId ? `Request ID: ${requestId}` : undefined,
        });
        setSubmissionError(message);

        if (error.errors) {
          for (const [field, fieldError] of Object.entries(error.errors)) {
            if (SIGN_UP_FIELD_NAMES.includes(field as keyof SignUpFormValues)) {
              const fieldMessage = normalizeFieldErrorMessage(fieldError);
              if (fieldMessage) {
                form.setError(field as keyof SignUpFormValues, {
                  type: "server",
                  message: fieldMessage,
                });
              }
            }
          }
        }

        if (errorName === "email-already-exists" || errorCode === "400102") {
          form.setError("email", {
            type: "server",
            message,
          });
          form.setFocus("email");
          return;
        }

        return;
      }

      toast.error(UNKNOWN_ERROR_MESSAGE);
      setSubmissionError(UNKNOWN_ERROR_MESSAGE);
    }
  });

  return (
    <div className="flex flex-col w-full gap-4 my-8">
      <h1 className="text-white text-md font-semibold py-1">
        Create New Account
      </h1>
      <Form {...form}>
        <form
          onSubmit={onSubmit}
          className="flex flex-col gap-4 w-xs"
          noValidate>
          {/* Temporarily hidden - Google login */}
          {/* <SignInWithGoogle /> */}
          {/* <Separator /> */}
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-label-dark">First Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="John"
                      autoComplete="given-name"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-label-dark">Last Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Doe"
                      autoComplete="family-name"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-label-dark">Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    inputMode="email"
                    placeholder="name@email.com"
                    autoComplete="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-label-dark">Password</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder="Enter the password"
                    autoComplete="new-password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-label-dark">
                  Re-Enter Password
                </FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder="Enter the password"
                    autoComplete="new-password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {showPasswordValidation && (
            <div className={containerClass}>
              <p className={itemClass(rules.hasMinLength)}>
                {rules.hasMinLength ? "✓" : "✕"} Has at least 8 Characters
              </p>
              <p className={itemClass(rules.hasUppercase)}>
                {rules.hasUppercase ? "✓" : "✕"} Has uppercase letters
              </p>
              <p className={itemClass(rules.hasLowercase)}>
                {rules.hasLowercase ? "✓" : "✕"} Has lowercase letters
              </p>
              <p className={itemClass(rules.hasNumber)}>
                {rules.hasNumber ? "✓" : "✕"} Has numbers
              </p>
              <p className={itemClass(rules.hasSpecialChar)}>
                {rules.hasSpecialChar ? "✓" : "✕"} Has non-alphanumeric
                characters
              </p>
              <p className={passwordsMatch ? "text-green-600" : "text-red-600"}>
                {passwordsMatch ? "✓" : "✕"} Passwords match
              </p>
            </div>
          )}
          <FormField
            control={form.control}
            name="referralCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-label-dark">
                  Referral Code (Optional)
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      placeholder="Enter referral code"
                      autoComplete="off"
                      {...field}
                    />
                    {referralValidationState === "validating" && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <LoaderIcon className="h-4 w-4 animate-spin text-gray-400" />
                      </div>
                    )}
                    {referralValidationState === "valid" && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      </div>
                    )}
                    {referralValidationState === "invalid" && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <XCircle className="h-4 w-4 text-red-500" />
                      </div>
                    )}
                  </div>
                </FormControl>
                {referralValidationState === "valid" && referrerName && (
                  <p className="text-xs text-green-600">
                    ✓ Valid referral code from {referrerName}
                  </p>
                )}
                {referralValidationState === "invalid" && (
                  <p className="text-xs text-red-600">
                    ✕ Invalid referral code
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="termsAccepted"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <div className="flex items-start gap-3">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) =>
                        field.onChange(checked === true)
                      }
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <p className="text-label-dark">
                      I accept the{" "}
                      <span>
                        <Link to="#" className="underline text-white">
                          Terms
                        </Link>
                      </span>{" "}
                      and{" "}
                      <span>
                        <Link to="#" className="underline text-white">
                          Conditions
                        </Link>
                      </span>
                    </p>
                  </div>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            variant="primary"
            className="mt-5 w-full"
            disabled={isPending}>
            {isPending ? "Creating account..." : "Sign Up"}
          </Button>
          {submissionError && (
            <p className="text-danger text-sm text-center">{submissionError}</p>
          )}
        </form>
      </Form>
    </div>
  );
}
